import React from "react";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import remarkGfm from "remark-gfm";
import { Box, Link, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

/**
 * Detect if text is in an RTL language (Arabic, Hebrew, Persian, etc.)
 * @param {string} text - Text to check
 * @returns {boolean} True if the text is primarily RTL
 */
const isRTLText = (text) => {
  if (!text || typeof text !== "string") return false;

  // Check if the text contains mostly RTL characters
  // Unicode ranges for common RTL scripts (Arabic, Hebrew, Persian, etc.)
  const rtlRegex =
    /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\u1E00-\u1EFF\uFB50-\uFDFF\uFE70-\uFEFF]/;

  // Check the first few non-whitespace characters
  const firstChars = text.replace(/\s+/g, "").substring(0, 10);

  // If we have multiple characters and the first character is RTL, consider it an RTL text
  return firstChars.length > 0 && rtlRegex.test(firstChars[0]);
};

const isArabic = (text) => {
  if (!text) return false;
  const arabicRegex =
    /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/g;
  const matches = text.match(arabicRegex);
  if (!matches) return false;

  const arabicCount = matches.length;
  const totalLetters = text.replace(/\s+/g, "").length || 1;
  return arabicCount / totalLetters >= 0.5;
};

/**
 * MarkdownRenderer component for rendering markdown content with code highlighting
 * @param {string} content - The markdown content to render
 * @param {boolean} compact - Whether to use compact styling for mobile
 */
const MarkdownRenderer = ({ content, compact = false }) => {
  const theme = useTheme();

  // Process content to ensure proper markdown formatting
  const processContent = (text) => {
    if (!text) return "";

    // Count the number of triple backticks to check if code blocks are balanced
    const codeBlockMarkers = (text.match(/```/g) || []).length;
    if (codeBlockMarkers % 2 !== 0) {
      // Code block is not closed, add a temporary closing marker
      return text + "\n```";
    }

    return text;
  };

  // Detect if the content is RTL
  // const isRTL = isArabic(content);
  const { i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  return (
    <Box
      sx={{
        overflowX: "hidden",
        width: "100%",
        wordBreak: "break-word",
        overflowWrap: "break-word",
        maxWidth: "100%",
        direction: isRTL ? "rtl" : "ltr",
        textAlign: "start",
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          code({ node, inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || "");
            const language = match ? match[1] : "";

            return !inline && match ? (
              <Box
                sx={{
                  position: "relative",
                  maxWidth: "100%",
                  my: compact ? 1 : 2,
                  overflowX: "auto",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    insetInlineEnd: 0,
                    bgcolor: "background.paper",
                    color: "text.secondary",
                    fontSize: compact ? "0.7rem" : "0.75rem",
                    px: compact ? 0.75 : 1,
                    py: compact ? 0.3 : 0.5,
                    borderTopRightRadius: theme.shape.borderRadius,
                    borderBottomLeftRadius: theme.shape.borderRadius,
                    opacity: 0.8,
                    fontFamily: "monospace",
                    textTransform: "lowercase",
                    zIndex: 1,
                  }}
                >
                  {language || "code"}
                </Box>
                <SyntaxHighlighter
                  style={atomDark}
                  language={language}
                  PreTag="div"
                  showLineNumbers={true}
                  wrapLines={true}
                  customStyle={{
                    margin: 0,
                    padding: theme.spacing(compact ? 1 : 1.5),
                    paddingTop: theme.spacing(compact ? 2 : 2.5),
                    borderRadius: theme.shape.borderRadius,
                    maxWidth: "100%",
                    overflow: "auto",
                    fontSize: compact ? "0.75rem" : "0.85rem",
                    direction: "ltr", // Always LTR for code blocks
                    textAlign: "start",
                  }}
                  {...props}
                >
                  {String(children).replace(/\n$/, "")}
                </SyntaxHighlighter>
              </Box>
            ) : (
              <code
                className={className}
                {...props}
                style={{
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255,255,255,0.1)"
                      : "rgba(0,0,0,0.05)",
                  padding: "2px 4px",
                  borderRadius: 4,
                  fontFamily: 'Consolas, Monaco, "Andale Mono", monospace',
                  fontSize: compact ? "0.8em" : "0.9em",
                  wordBreak: "break-word",
                  overflowWrap: "break-word",
                  whiteSpace: "pre-wrap",
                  direction: "ltr", // Always LTR for inline code
                  textAlign: "start",
                }}
              >
                {children}
              </code>
            );
          },
          a: ({ node, ...props }) => (
            <Link
              {...props}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                wordBreak: "break-word",
                overflowWrap: "break-word",
              }}
            />
          ),
          p: ({ node, ...props }) => (
            <Typography
              variant={compact ? "body2" : "body1"}
              sx={{
                mb: compact ? 1 : 1.5,
                overflow: "hidden",
                maxWidth: "100%",
                wordBreak: "break-word",
                direction: isRTL ? "rtl" : "ltr",
                textAlign: "start",
              }}
              {...props}
            />
          ),
          h1: ({ node, ...props }) => (
            <Typography
              variant={compact ? "h5" : "h4"}
              sx={{ mt: compact ? 1.5 : 2, mb: compact ? 0.75 : 1 }}
              {...props}
            />
          ),
          h2: ({ node, ...props }) => (
            <Typography
              variant={compact ? "h6" : "h5"}
              sx={{ mt: compact ? 1.5 : 2, mb: compact ? 0.75 : 1 }}
              {...props}
            />
          ),
          h3: ({ node, ...props }) => (
            <Typography
              variant={compact ? "subtitle1" : "h6"}
              sx={{ mt: compact ? 1.5 : 2, mb: compact ? 0.75 : 1 }}
              {...props}
            />
          ),
          ul: ({ node, ...props }) => (
            <Box
              component="ul"
              sx={{
                paddingInlineStart: compact ? 1.5 : 2,
                mb: compact ? 1.5 : 2,
                "& li": {
                  mb: compact ? 0.5 : 0.75,
                },
                maxWidth: "100%",
              }}
              {...props}
            />
          ),
          ol: ({ node, ...props }) => (
            <Box
              component="ol"
              sx={{
                paddingInlineStart: compact ? 1.5 : 2,
                mb: compact ? 1.5 : 2,
                "& li": {
                  mb: compact ? 0.5 : 0.75,
                },
                maxWidth: "100%",
              }}
              {...props}
            />
          ),
          li: ({ node, ...props }) => (
            <Box
              component="li"
              sx={{
                mb: compact ? 0.3 : 0.5,
                wordBreak: "break-word",
                fontSize: compact ? "0.875rem" : "inherit",
              }}
              {...props}
            />
          ),
          blockquote: ({ node, ...props }) => (
            <Box
              component="blockquote"
              sx={{
                paddingInlineStart: 2,
                borderInlineStart: "4px solid",
                borderColor:
                  theme.palette.mode === "dark"
                    ? theme.palette.grey[700]
                    : theme.palette.grey[300],
                fontStyle: "italic",
                my: 2,
                color: theme.palette.text.secondary,
                maxWidth: "100%",
              }}
              {...props}
            />
          ),
          table: ({ node, ...props }) => (
            <Box
              sx={{
                overflow: "auto",
                maxWidth: "100%",
                mb: 2,
              }}
            >
              <Box
                component="table"
                sx={{
                  borderCollapse: "collapse",
                  width: "100%",
                  "& th, & td": {
                    border: "1px solid",
                    borderColor:
                      theme.palette.mode === "dark"
                        ? theme.palette.grey[700]
                        : theme.palette.grey[300],
                    p: 1,
                    minWidth: "100px",
                  },
                  "& th": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? theme.palette.grey[800]
                        : theme.palette.grey[100],
                  },
                }}
                {...props}
              />
            </Box>
          ),
          strong: ({ node, ...props }) => (
            <Box
              component="strong"
              sx={{
                fontWeight: "bold",
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.primary.light
                    : theme.palette.primary.dark,
              }}
              {...props}
            />
          ),
          img: ({ node, ...props }) => (
            <Box
              component="img"
              sx={{
                maxWidth: "100%",
                height: "auto",
                my: 2,
                borderRadius: theme.shape.borderRadius,
              }}
              {...props}
              loading="lazy"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ),
        }}
      >
        {processContent(content)}
      </ReactMarkdown>
    </Box>
  );
};

export default MarkdownRenderer;
